import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import { Emitter } from '@patsnap/tahiti-core';
import { EmbodimentAddApi } from '@/infrastructure/api-functions/core-drafting-api/embodiment-add-api';
import { CurrentTaskService } from '@/services/current-task/current-task-service';
import { EmbodimentModel } from '../../models/embodiment-model';
import { EmbodimentHistoryService } from '../embodiment-history-service';
import { EmbodimentRulesService } from '../embodiment-rules-service';
import type { EmbodimentService } from '../embodiment-service';
import type { EmbodimentState, EmbodimentItem, EmbodimentRule } from '../../types';

@injectable()
export class EmbodimentServiceImpl implements EmbodimentService {
    private _onModelDataChangeEmitter = new Emitter<EmbodimentState>();
    public readonly onStateChange = this._onModelDataChangeEmitter.event;

    // eslint-disable-next-line max-params
    constructor(
        @inject(EmbodimentModel)
        private _model: EmbodimentModel,
        @inject(EmbodimentAddApi)
        private _embodimentAddApi: EmbodimentAddApi,
        @inject(CurrentTaskService)
        private _currentTaskService: CurrentTaskService,
        @inject(EmbodimentHistoryService)
        private _embodimentHistoryService: EmbodimentHistoryService,
        @inject(EmbodimentRulesService)
        private _embodimentRulesService: EmbodimentRulesService,
    ) {
        this._model.onStatesChange(this._emitModelDataChange.bind(this));
        this._embodimentHistoryService.onToggleHistory(this._onToggleHistory.bind(this));
        this._embodimentRulesService.onSubmitRules(this._onSubmitRules.bind(this));
    }

    private _emitModelDataChange(): void {
        this._onModelDataChangeEmitter.fire(this.getModelData());
    }

    public getModelData(): EmbodimentState {
        return this._model.modelData;
    }

    private _updateModelData(value: Partial<EmbodimentState>): void {
        this._model.update(state => ({
            ...state,
            ...value,
        }));
    }

    private _onToggleHistory(item: EmbodimentItem): void {
        this._updateModelData({
            content: item.content,
            rules: item.rules,
        });
    }

    private _onSubmitRules(rules: EmbodimentRule[]): void {
        this._updateModelData({
            rules,
        });
    }

    public async generateEmbodiment(): Promise<void> {
        // 更新模型状态，清空内容
        this._updateModelData({ content: '', isGenerating: true });

        const stream = this._embodimentAddApi.embodimentAdd({
            task_id: this._currentTaskService.taskId,
            rules: this.getModelData().rules.map(rule => ({
                concept: rule.claimFeature.feature,
                replacement: rule.keyword.value,
                num: rule.claimFeature.num,
                expand_type: rule.keyword.type,
            })),
        })
            .onMessage(res => {
                this._updateModelData({ content: res.data.content });
            })
            .onFinish(() => {
                this._updateModelData({ isGenerating: false });
                this._embodimentHistoryService.pushHistory({
                    content: this.getModelData().content,
                    rules: this.getModelData().rules,
                });
            })
            .onError(() => {
                this._updateModelData({ isGenerating: false });
            });

        await stream.start();
    }

    public updateEmbodimentContent(content: string): void {
        this._updateModelData({ content });
    }

    public resetEmbodiment(): void {
        this._updateModelData({
            content: '',
            rules: [],
            isGenerating: false,
        });

        this._embodimentHistoryService.resetHistory();
        this._embodimentRulesService.resetRules();
    }
}
